import { BlockMath, InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

const VARIANTS = {
  parallel: [
    { offset: 2, b1: 3, c1: -4, a2: 4, b2: 6, c2: 1, answer: 0 },
    { offset: -1, b1: 2, c1: 3, a2: 3, b2: 6, c2: -5, answer: 2 },
    { offset: 3, b1: 2, c1: 1, a2: 2, b2: 4, c2: -3, answer: -2 },
  ],
  perpendicular: [
    { offset: 1, b1: 2, c1: -3, a2: 2, b2: -4, c2: 1, answer: 3 },
    { offset: -2, b1: 3, c1: 1, a2: 3, b2: -2, c2: 4, answer: 4 },
    { offset: 2, b1: 1, c1: -5, a2: 1, b2: -3, c2: 2, answer: 1 },
  ],
};

const signed = (value) => value >= 0 ? `+${value}` : `${value}`;
const parameterCoefficient = (offset) => offset === 0 ? "m" : `m${signed(offset)}`;
const formatLine = ({ offset, b1, c1 }) => `(${parameterCoefficient(offset)})x${signed(b1)}y${signed(c1)}=0`;
const formatFixedLine = ({ a2, b2, c2 }) => `${a2}x${signed(b2)}y${signed(c2)}=0`;
const signedTerm = (value) => value === 0 ? "" : signed(value);
const formatLinearEquation = (coefficient, constants) => `${coefficient}m${constants.map(signedTerm).join("")}=0`;

export function generateTask({ relation = "parallel" } = {}) {
  const safeRelation = relation === "perpendicular" ? "perpendicular" : "parallel";
  const variants = VARIANTS[safeRelation];
  const data = variants[Math.floor(Math.random() * variants.length)];
  return {
    type: "FindParameterGeneralLineRelationTask",
    data: { ...data, relation: safeRelation },
    answer: data.answer,
  };
}

export function renderTask({ data }) {
  const relationLabel = data.relation === "parallel" ? "równoległe" : "prostopadłe";
  return (
    <div className="space-y-2">
      <p>Dane są proste w postaci ogólnej:</p>
      <BlockMath math={String.raw`k: ${formatLine(data)}`} />
      <BlockMath math={String.raw`l: ${formatFixedLine(data)}`} />
      <p>Dla jakiej wartości parametru <InlineMath math="m" /> proste są <strong>{relationLabel}</strong>?</p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return <FractionInput value={userAnswer || ""} onChange={onChange} disabled={disabled} label="m" />;
}

export function checkAnswer({ answer }, userAnswer) {
  return Number.isFinite(userAnswer?.parsed) && Math.abs(userAnswer.parsed - answer) < 0.001;
}

export function renderAnswer({ answer }) {
  return <InlineMath math={`m=${answer}`} />;
}

export function renderExplanation({ data }) {
  const condition = data.relation === "parallel"
    ? String.raw`A_{1}B_{2}-A_{2}B_{1}=0`
    : String.raw`A_{1}A_{2}+B_{1}B_{2}=0`;
  const substitution = data.relation === "parallel"
    ? String.raw`${data.b2}(${parameterCoefficient(data.offset)})-${data.a2}\cdot${data.b1}=0`
    : String.raw`${data.a2}(${parameterCoefficient(data.offset)})+${data.b1}\cdot(${data.b2})=0`;
  const coefficient = data.relation === "parallel" ? data.b2 : data.a2;
  const firstConstant = coefficient * data.offset;
  const secondConstant = data.relation === "parallel" ? -data.a2 * data.b1 : data.b1 * data.b2;
  const constant = firstConstant + secondConstant;
  const rightSide = -constant;
  return (
    <div className="space-y-2">
      <p><strong>Krok 1.</strong> Dla prostych w postaci ogólnej stosujemy warunek z karty wzorów:</p>
      <BlockMath math={condition} />
      <p><strong>Krok 2.</strong> Podstawiamy współczynniki przy <InlineMath math="x" /> i <InlineMath math="y" />.</p>
      <BlockMath math={substitution} />
      <BlockMath math={formatLinearEquation(coefficient, [firstConstant, secondConstant])} />
      <BlockMath math={String.raw`${coefficient}m=${rightSide}`} />
      <BlockMath math={String.raw`m=\frac{${rightSide}}{${coefficient}}=${data.answer}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw, "m")} />;
}
